import supabase from './supabase';

const billingApiUrl = (import.meta.env.VITE_BILLING_API_URL || '').replace(/\/$/, '');

export const billingEnabled = Boolean(billingApiUrl);

const createResult = (status, values = {}) => ({
  ok: status === 'success',
  status,
  ...values,
});

async function getAccessToken() {
  if (!supabase) return null;

  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session?.access_token || null;
}

async function request(path, options = {}) {
  if (!billingEnabled) {
    return createResult('unavailable', {
      message: 'Studio Plus purchases are not configured yet.',
    });
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return createResult('unavailable', {
        message: 'A verified account session is required for billing.',
      });
    }

    const response = await fetch(`${billingApiUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...options.headers,
      },
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return createResult('error', {
        message: data.message || 'The billing service could not complete this request.',
      });
    }

    return createResult('success', { data });
  } catch {
    return createResult('error', {
      message: 'Billing could not be reached. Please try again later.',
    });
  }
}

export async function getMembership() {
  const response = await request('/membership', { method: 'GET' });
  if (!response.ok) return response;

  const { plan, verified, status = 'active' } = response.data || {};
  if (verified !== true || !['free', 'plus'].includes(plan)) {
    return createResult('error', {
      message: 'The server did not return a verified membership.',
    });
  }

  return createResult('success', {
    membership: { plan, status, verified: true },
  });
}

export async function startCheckout(cadence) {
  if (!['monthly', 'annual'].includes(cadence)) {
    return createResult('error', {
      message: 'Choose monthly or annual billing.',
    });
  }

  const response = await request('/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan: 'plus', cadence }),
  });
  if (!response.ok) return response;

  const checkoutUrl = response.data?.checkoutUrl;
  if (!checkoutUrl?.startsWith('https://')) {
    return createResult('error', {
      message: 'The billing server did not return a secure checkout URL.',
    });
  }

  return createResult('success', { checkoutUrl });
}

export async function openCustomerPortal() {
  const response = await request('/portal', { method: 'POST' });
  if (!response.ok) return response;

  const portalUrl = response.data?.portalUrl;
  if (!portalUrl?.startsWith('https://')) {
    return createResult('error', {
      message: 'The billing server did not return a secure portal URL.',
    });
  }

  return createResult('success', { portalUrl });
}

export const billingService = {
  billingEnabled,
  getMembership,
  startCheckout,
  openCustomerPortal,
};

export default billingService;